// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module MetricCard
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS metric card for a single computed
 * summary metric. The card formats percentages and raw counts while exposing
 * trend coloring from the shared analytics tokens.
 */

import { type MetricSummary, TREND_COLORS } from "@geenius/analytics";
import { Badge, Card, CardContent } from "@geenius/ui/solidjs-css";
import { type Component, Show } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the SolidJS CSS metric card.
 *
 * @property metric Metric summary payload rendered by the card.
 * @property class Optional class name merged into the card wrapper.
 */
export interface MetricCardProps {
  metric: MetricSummary;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders a SolidJS CSS metric card for one summary metric.
 *
 * @param props Summary metric payload for the card.
 * @returns A metric card element with formatted value and optional trend.
 */
export const MetricCard: Component<MetricCardProps> = (props) => {
  const copy = createAnalyticsCopy();
  const formattedValue = (): string =>
    props.metric.format === "percent"
      ? copy.formatPercent(props.metric.value)
      : props.metric.format === "duration"
        ? copy.formatDuration(props.metric.value)
        : copy.formatNumber(props.metric.value);

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardContent class="gn-analytics-metric">
          <ErrorState
            error={props.error}
            title={props.metric.label}
            onRetry={props.onRetry}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card class={props.class}>
      <CardContent class="gn-analytics-metric">
        <span class="gn-analytics-metric__label">{props.metric.label}</span>
        <span class="gn-analytics-metric__value">{formattedValue()}</span>
        <Show when={props.metric.change !== undefined}>
          <Badge
            variant={
              props.metric.trend === "up"
                ? "success"
                : props.metric.trend === "down"
                  ? "danger"
                  : "secondary"
            }
            size="sm"
          >
            <span
              class="gn-analytics-metric__change"
              data-trend={props.metric.trend}
              style={{ color: TREND_COLORS[props.metric.trend] }}
            >
              {props.metric.trend === "up"
                ? "↑"
                : props.metric.trend === "down"
                  ? "↓"
                  : "→"}{" "}
              {copy.formatPercent(props.metric.changePercent ?? 0)}
            </span>
          </Badge>
        </Show>
      </CardContent>
    </Card>
  );
};
